// https://www.freecodecamp.org/news/build-a-react-hooks-front-end-app-with-routing-and-authentication/
import axios from "axios"
import jwt_decode from "jwt-decode"

import { queryClient } from "../index"
import { storeApi, resetAllStores } from "../store"

/**
 * Constructs the base URL for authentication API calls.
 *
 * @returns {string} The base URL for the authentication API.
 */
export const getAuthBaseUrl = (): string => {
  const [apiProtocol, apiHostname, apiPort] = [
    storeApi.getState().apiProtocol,
    storeApi.getState().apiHostname,
    storeApi.getState().apiPort,
  ]
  return `${apiProtocol}://${apiHostname}:${apiPort}`
}

// Define a type for all available feature keys
export type FeaturePermissionKey = keyof typeof Auth.featurePermissions

/**
 * Auth class for handling authentication-related operations.
 */
class Auth {
  axios_obj: any

  constructor() {
    this.axios_obj = axios.create({
      responseType: "json",
    })
  }

  /**
   * Retrieves the username from local storage.
   *
   * @returns {string | null} The username or null if not found.
   */
  getUsername = (): string | null => localStorage.getItem("username") || null

  /**
   * Retrieves the user ID from local storage.
   *
   * @returns {string | null} The user ID or null if not found.
   */
  getUserId = (): string | null => localStorage.getItem("user_id") || null

  /**
   * Retrieves the first name from local storage.
   *
   * @returns {string | null} The first name or null if not found.
   */
  getFirstName = (): string | null => localStorage.getItem("first_name") || null

  /**
   * Retrieves the last name from local storage.
   *
   * @returns {string | null} The last name or null if not found.
   */
  getLastName = (): string | null => localStorage.getItem("last_name") || null

  /**
   * Retrieves the full name by combining first and last names.
   *
   * @returns {string} The full name.
   */
  getFullName = (): string => `${this.getFirstName() || ""} ${this.getLastName() || ""}`

  /**
   * Retrieves permissions from local storage.
   *
   * @returns {string[] | null} The permissions or null if not found.
   */
  getPermissions = (): string[] | null => JSON.parse(localStorage.getItem("permissions") as string) || null

  /**
   * Sets the access token in local storage.
   *
   * @param {string} access_token - The access token to set.
   */
  setAccessToken = (access_token: string) => localStorage.setItem("access_token", access_token)

  /**
   * Retrieves the access token from local storage.
   *
   * @returns {string | null} The access token or null if not found.
   */
  getAccessToken = (): string | null => localStorage.getItem("access_token") || null

  /**
   * Retrieves the refresh token from local storage.
   *
   * @returns {string | null} The refresh token or null if not found.
   */
  getRefreshToken = (): string | null => localStorage.getItem("refresh_token") || null

  /**
   * Constructs the authorization header value using the access token.
   *
   * @returns {string} The authorization header value.
   */
  getAuthHeaderValue = (): string => "Bearer " + this.getAccessToken()

  /**
   * Retrieves the selected branch from local storage.
   *
   * @returns {string | null} The selected branch or null if not found.
   */
  getSelectedBranch = (): string | null => localStorage.getItem("selected_branch") || null

  /**
   * Checks if the refresh token is valid based on its expiry date.
   *
   * @returns {boolean} True if the refresh token is valid, otherwise false.
   */
  checkIfRefreshTokenIsValid = (): boolean => {
    const currentTime = Date.now() / 1000
    const expiryDate = parseInt(localStorage.getItem("refresh_expiry_date") || "")
    return currentTime < expiryDate
  }

  /**
   * Constructs the authorization header for API requests.
   *
   * @returns {object} The authorization header object.
   */
  getAuthHeader = (): object => {
    return { headers: { Authorization: "Bearer " + this.getAccessToken() } }
  }

  /**
   * Resets the selected branch in local storage and the store.
   */
  resetSelectedBranch = () => localStorage.removeItem("branches-store")

  // Map of feature keys to their corresponding permission checks
  static featurePermissions = {
    // Analytics
    chatbot: "can_access_azka_bot",
    cars_analytics: "analytics_can_access_cars",
    heatmap: "analytics_can_access_heatmap",
    missing_data: "analytics_can_access_missing_data",
    people_count: "analytics_can_access_people_count",
    zones_occupancy: "analytics_can_access_zones_occupancy",
    mcp: "analytics_can_access_mcp",
    // Admin
    api_integration: "admin_can_access_api_token",
    branches: "admin_can_access_branches",
    cameras: "admin_can_access_camera",
    cars_admin: "admin_can_access_cars",
    categories: "admin_can_access_categories",
    entities: "admin_can_access_entities",
    floor: "admin_can_access_floor",
    intrusion_setup: "admin_can_access_intrusion_setup",
    motion_setup: "admin_can_access_motion_setup",
    nodes: "admin_can_access_nodes",
    system_status: "admin_can_access_system_status",
    users: "admin_can_access_users",
    defect_detection_setup: "admin_can_access_defect_detection_setup",
    // Security
    security_camera_stream: "security_can_access_camera_stream",
    cars_security: "security_can_access_cars",
    security_events: "security_can_access_events",
    facial_recognition: "security_can_access_facial_recognition",
    // Marketing
    campaign: "marketing_can_access_campaign",
    // Multiple Branches
    multiple_branches: "admin_can_access_multiple_branches",
    // Industrial
    defect_detection: "industrial_can_access_defect_detection",
  } as const

  /**
   * Check if user has permission for a specific feature
   * @param {FeaturePermissionKey} featureKey - The key of the feature to check
   * @returns {boolean} Whether the user has permission
   */
  hasFeaturePermission = (featureKey: FeaturePermissionKey): boolean => {
    const permissionKey = Auth.featurePermissions[featureKey]
    if (!permissionKey) return false
    return localStorage.getItem("permissions")?.includes(permissionKey) || false
  }

  /**
   * Logs in the user with the provided credentials.
   *
   * @param {object} credentials - The login credentials.
   * @param {string} credentials.username - The username.
   * @param {string} credentials.password - The password.
   * @returns {Promise<void>} A promise that resolves when the login is successful.
   */
  async login(credentials: { username: string; password: string }): Promise<void> {
    try {
      let res = await this.axios_obj.post(getAuthBaseUrl() + "/api/token/", credentials)
      let access_token = res.data.access
      localStorage.setItem("access_token", access_token)
      let refresh_token = res.data.refresh
      localStorage.setItem("refresh_token", refresh_token)
      const access_payload = jwt_decode(access_token) as {
        permissions: string[]
        exp: number
        username: string
        user_id: string
        first_name: string
        last_name: string
      }

      localStorage.setItem("permissions", JSON.stringify(access_payload.permissions))
      localStorage.setItem("access_expiry_date", access_payload.exp.toString())
      const refresh_payload = jwt_decode(refresh_token) as { exp: number }

      localStorage.setItem("refresh_expiry_date", refresh_payload.exp.toString())
      localStorage.setItem("username", access_payload.username)
      localStorage.setItem("user_id", access_payload.user_id)
      localStorage.setItem("first_name", access_payload.first_name)
      localStorage.setItem("last_name", access_payload.last_name)
      // if there is a stored branch in local storage => remove it.
      if (this.getSelectedBranch()) {
        this.resetSelectedBranch()
      }

      document.cookie = `authorization=${access_token ? access_token : this.getAccessToken()}; path=/`
    } catch (e) {
      throw e
    }
  }

  /**
   * Logs out the user, clears local storage, and optionally redirects to the login page.
   *
   * @param {boolean} [redirect=false] - Whether to redirect to the login page.
   * @param {function} [navigate] - The navigation function to use for redirection.
   */
  logOut(redirect: boolean = false, navigate?: (path: string, options: { replace: boolean }) => void) {
    // Reset all stores to initial state
    resetAllStores()

    localStorage.clear()

    // Clear cache
    queryClient.clear()

    document.cookie = "authorization=; path=/; Max-Age=-99999999;"

    if (redirect && navigate) {
      // has to be imported for navigation to work as expected
      navigate("/login", { replace: true })
    }
  }
  /**
   * Checks if the user is authenticated based on the validity of the refresh token.
   *
   * @returns {boolean} True if the user is authenticated, otherwise false.
   */
  isAuthenticated = (): boolean => this.checkIfRefreshTokenIsValid()
}
const auth = new Auth()
export default auth
